/**
 * acronymExtractor — find which dictionary acronyms appear in a
 * report's rendered narrative, so the auto-glossary only lists what
 * the reader will actually encounter.
 *
 * Strategy:
 *   1. JSON-stringify the report so every nested string field is
 *      visible to a single pass. Cheap; the alternative (recursive
 *      walk) saves nothing for our text-density and adds bug surface.
 *   2. For each dictionary key, run a `\bKEY\b` regex. Word-boundary
 *      matching is essential — without it, `EA` would match every
 *      `Years` / `Reach` / `Headers`, polluting the glossary.
 *   3. Return the matched keys sorted alphabetically (display order
 *      in the glossary; stable across re-renders).
 *
 * Pure / synchronous so the result can be memoised by callers
 * without re-running on every render.
 */
import type { SiteRagReport } from '../types/siteRag';
import { ACRONYMS } from '../config/acronyms';

/**
 * Scan a report for which dictionary acronyms appear. Returns the
 * matched keys in alphabetical order.
 *
 * `report` is intentionally typed loose (`unknown`-friendly) so
 * tests can pass a fixture object without the full SiteRagReport
 * shape. The JSON-stringify pass treats any value the same way.
 */
export function extractAcronymsFromReport(report: SiteRagReport | Record<string, unknown> | null | undefined): string[] {
  if (!report) return [];
  let text: string;
  try {
    text = JSON.stringify(report);
  } catch {
    return [];
  }
  const found = new Set<string>();
  for (const key of Object.keys(ACRONYMS)) {
    // Anchor on word boundaries — without \b, "EA" matches "Years"
    // and "Reach"; "PGA" matches "tampered". Acronym keys are
    // ALL CAPS but the regex isn't /i because case-mixed words like
    // "Esa" should NOT match the ESA key.
    const re = new RegExp(`\\b${escapeRegExp(key)}\\b`);
    if (re.test(text)) found.add(key);
  }
  return [...found].sort();
}

/** Escape regex metacharacters in a key — defensive in case a future
 *  acronym entry includes a `.` or other special char. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
